import { createWidgetShapeError, WidgetShapeErrorCode } from './errors';
import { PlatformSDKShape } from '../PlatformSDKShape';
import { BaseSDKShape } from '../BaseSDKShape';
import { getFontFamiliesFromFonts } from '../inputs/utils';
import { ComponentRef } from '@wix/public-editor-platform-interfaces';

export class WidgetScopedSDK {
  constructor(
    private compRef: ComponentRef,
    private privateAPI: any,
  ) {}

  async getProp(propName: string): Promise<string> {
    const props = await this.privateAPI.refComponents.getProps(this.compRef);
    return props?.[propName];
  }

  async setProp(propName: string, value: string) {
    await this.privateAPI.refComponents.setProps(this.compRef, {
      [propName]: value,
    });
  }

  async getDesignPreset() {
    return this.privateAPI.designPresets.getDesignPresetName(this.compRef);
  }

  async setDesignPreset(designPresetName: string) {
    await this.privateAPI.designPresets.setDesignPresetByName(
      this.compRef,
      designPresetName,
    );
  }

  async getNestedWidget(selector: `#${string}`) {
    const childCompRef =
      await this.privateAPI.refComponents.findComponentBySelector(
        this.compRef,
        selector,
      );

    if (!childCompRef) {
      return null;
    }

    return new WidgetScopedSDK(childCompRef, this.privateAPI);
  }
}

export class WidgetSDKShape extends BaseSDKShape {
  async #getSelectedComponentRef() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const refs = await privateAPI.components.getSelectedComponents();
    const compRef = refs[0];

    if (!compRef) {
      throw createWidgetShapeError(WidgetShapeErrorCode.UndefinedCompRef);
    }

    return compRef;
  }

  async setPreloadFonts(fonts: string[]): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        'not available for the current component type',
      );
    }

    const fontFamilies = getFontFamiliesFromFonts(fonts);

    await privateAPI.customElement.setPreloadFonts(compRef, fontFamilies);
  }

  async getProp(propName: string): Promise<string> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      return new WidgetScopedSDK(compRef, privateAPI).getProp(propName);
    } else {
      return privateAPI.customElement.getAttribute(compRef, propName);
    }
  }

  async setProp(propName: string, value: string) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    if (await privateAPI.refComponents.isRefComponent(compRef)) {
      return new WidgetScopedSDK(compRef, privateAPI).setProp(propName, value);
    } else {
      await privateAPI.customElement.setAttribute(compRef, propName, value);
    }
  }

  async getDesignPreset() {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    if (!(await privateAPI.refComponents.isRefComponent(compRef))) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        'not available for the current component type',
      );
    }

    return new WidgetScopedSDK(compRef, privateAPI).getDesignPreset();
  }

  async setDesignPreset(designPresetName: string) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    if (!(await privateAPI.refComponents.isRefComponent(compRef))) {
      throw createWidgetShapeError(
        WidgetShapeErrorCode.NotAvailableMethod,
        'not available for the current component type',
      );
    }

    return new WidgetScopedSDK(compRef, privateAPI).setDesignPreset(
      designPresetName,
    );
  }

  async getNestedWidget(selector: `#${string}`) {
    const privateAPI = await this.getPlatformPrivateAPI();
    const compRef = await this.#getSelectedComponentRef();

    return new WidgetScopedSDK(compRef, privateAPI).getNestedWidget(selector);
  }
}

const widgetShape = new PlatformSDKShape(WidgetSDKShape)
  .withPublicMethod('getNestedWidget')
  .withPublicMethod('setDesignPreset')
  .withPublicMethod('getDesignPreset')
  .withPublicMethod('setProp')
  .withPublicMethod('getProp')
  .withPublicMethod('setPreloadFonts');

export default widgetShape.build();
